<template>
  <section class="atlas-opt-in-hero" :class="{ 'atlas-opt-in-hero--mini': isMiniView }">
    <div class="atlas-opt-in-hero__gradient-bg"></div>
    <div class="atlas-opt-in-hero__container">
      <div class="atlas-opt-in-hero__content" :class="{ 'atlas-opt-in-hero__content--mini': isMiniView }">
        <div class="atlas-opt-in-hero__text-section">
          <h2 v-if="isMiniView" class="global-h2 standard-page-header__title atlas-opt-in-hero__mini-header">
            <RouterLink :to="{ name: 'AtlasOptInLanding', params: { customerId } }"> Atlas Social</RouterLink>
          </h2>
          <h1 v-else class="atlas-opt-in-hero__headline">Keep your social channels active with authentic local content</h1>
          <p v-if="isMiniView" class="collab-tiles-section__subtitle">
            Keep your social channels active with authentic local content.
            <span v-if="showSnapshotsMessage" style="display: block; margin-top: 10px">Everything you loved about Social Snapshots, now fully automated!</span>
          </p>
          <p v-else class="atlas-opt-in-hero__subheading">Shrpa turns your destination's stories &amp; visuals into a consistent, on-brand social feed without adding to your team's workload.</p>
          <div class="atlas-opt-in-hero__buttons">
            <SrpButton :size="isMiniView ? 'normal' : 'big'" color="teal" fill="solid" :isDisabled="hasOptedIn || isProcessing" @click="trialClicked">
              {{ isProcessing ? "Signing up..." : "Try it Today" }}
            </SrpButton>
            <div v-if="hasOptedIn" class="atlas-opt-in-hero__opted-in-message">
              <strong>Signed up - We'll reach out shortly!</strong>
            </div>
          </div>
          <div class="atlas-opt-in-hero__tagline">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="atlas-opt-in-hero__tagline-icon">
              <path d="M12 2a10 10 0 1 0 10 10A10.011 10.011 0 0 0 12 2Zm4.3 8.3-4.59 4.59a1 1 0 0 1-1.42 0L7.7 12.3a1 1 0 1 1 1.41-1.41l1.18 1.17 3.88-3.88a1 1 0 0 1 1.41 1.42Z" />
            </svg>
            <span>Rights-ready UGC. Simple approvals. Reliable posting.</span>
          </div>
        </div>

        <div class="atlas-opt-in-hero__mockup-section" :class="{ 'atlas-opt-in-hero__mockup-section--mini': isMiniView }">
          <div class="atlas-opt-in-hero__mockup-card">
            <div class="atlas-opt-in-hero__calendar-widget">
              <div class="atlas-opt-in-hero__calendar-header">
                <div class="atlas-opt-in-hero__calendar-title-area">
                  <div class="atlas-opt-in-hero__calendar-icon"></div>
                  <span class="atlas-opt-in-hero__calendar-title">{{ currentMonth }}</span>
                </div>
                <span class="atlas-opt-in-hero__calendar-badge">Auto-scheduled</span>
              </div>
              <div class="atlas-opt-in-hero__calendar-grid">
                <div
                  v-for="i in 35"
                  :key="i"
                  :class="{
                    'atlas-opt-in-hero__calendar-cell': true,
                    'atlas-opt-in-hero__calendar-cell--highlighted': highlightCells.includes(i),
                  }"
                >
                  <div class="atlas-opt-in-hero__calendar-cell-content"></div>
                </div>
              </div>
            </div>
            <div class="atlas-opt-in-hero__status-items">
              <div v-for="status in statusItems" :key="status" class="atlas-opt-in-hero__status-item">
                <div class="atlas-opt-in-hero__status-icon"></div>
                <span class="atlas-opt-in-hero__status-text">{{ status }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from "vue";
import FeatureFlagCache from "@logic/FeatureFlagCache";
import ContentCalendarRepo from "@repos/ContentCalendarRepo";
import SrpButton from "@components/ui/SrpButton.vue";

const props = withDefaults(
  defineProps<{
    isMiniView?: boolean;
    customerId?: string;
  }>(),
  {
    isMiniView: false,
    customerId: "",
  }
);

const currentMonth = computed(() => {
  return new Date().toLocaleString("en-US", { month: "long" });
});

const showSnapshotsMessage = computed(() => {
  const cutoffDate = new Date("2026-02-01");
  return new Date() <= cutoffDate;
});

const highlightCells = ref([2, 5, 9, 14, 18, 22, 27]);
const statusItems = ref(["4 posts approved for next week", "12 new community uploads", "Creator Visit scheduled"]);

const hasOptedIn = ref(false);
const isProcessing = ref(false);

onMounted(async () => {
  hasOptedIn.value = await FeatureFlagCache.GetFeature("atlas-opt-in");
});

const trialClicked = async () => {
  if (isProcessing.value || hasOptedIn.value) return;

  isProcessing.value = true;

  await ContentCalendarRepo.optInToAtlas(props.customerId);
  await FeatureFlagCache.Refresh(props.customerId);
  hasOptedIn.value = true;
  isProcessing.value = true;
};
</script>

<style scoped lang="scss">
@import "@/scss/screen-size-ranges.scss";

.atlas-opt-in-hero {
  position: relative;
  // overflow: hidden;
  padding: 64px 0;

  @media (max-width: $tablet-max-width) {
    padding: 40px 0;
  }

  &--mini {
    padding: 0;
  }

  &__gradient-bg {
    position: absolute;
    inset: 0;
    z-index: -1;
    background: linear-gradient(to bottom, #f7faf9 0%, transparent 40%);
  }

  &__container {
    max-width: 1152px;
    margin: 0 auto;
    // padding: 0 16px;
  }

  &__content {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 40px;
    align-items: center;

    @media (max-width: $tablet-max-width) {
      grid-template-columns: 1fr;
    }

    &--mini {
      align-items: start;
    }
  }

  &__text-section {
    display: flex;
    flex-direction: column;
  }

  &__headline {
    font-size: 48px;
    font-weight: 700;
    line-height: 1.1;
    letter-spacing: -0.02em;
    color: #111827;
    margin: 0 0 16px 0;

    @media (max-width: $tablet-max-width) {
      font-size: 36px;
    }
  }

  &__subheading {
    font-size: 18px;
    line-height: 1.6;
    color: #4b5563;
    margin: 0 0 24px 0;

    &--mini {
      font-size: 18px;
      line-height: 1.6;
      color: #4b5563;
    }
  }

  &__buttons {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
    margin-bottom: 24px;
  }

  &__opted-in-message {
    font-size: 16px;

    strong {
      font-weight: 700;
    }
  }

  &__tagline {
    display: flex;
    align-items: center;
    gap: 7px;
    font-size: 14px;
    color: #4b5563;
  }

  &__tagline-icon {
    width: 16px;
    height: 16px;
    flex-shrink: 0;
  }

  &__mockup-section {
    position: relative;

    &--mini {
      max-height: 250px;
      overflow: hidden;

      @media (max-width: $tablet-max-width) {
        display: none;
      }

      &::after {
        content: "";
        position: absolute;
        bottom: 0;
        left: 0;
        right: 0;
        height: 60px;
        background: linear-gradient(to bottom, transparent, white);
        pointer-events: none;
      }
    }
  }

  &__mockup-card {
    border-radius: 16px;
    border: 1px solid #e5e7eb;
    background: white;
    padding: 16px;
    box-shadow:
      0 20px 25px -5px rgba(0, 0, 0, 0.1),
      0 10px 10px -5px rgba(0, 0, 0, 0.04);
  }

  &__calendar-widget {
    border-radius: 12px;
    background: #f3f4f6;
    padding: 12px;
  }

  &__calendar-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 12px;
  }

  &__calendar-title-area {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  &__calendar-icon {
    width: 20px;
    height: 20px;
    border-radius: 4px;
    background: #d1d5db;
  }

  &__calendar-title {
    font-size: 12px;
    color: #6b7280;
  }

  &__calendar-badge {
    font-size: 12px;
    color: #6b7280;
  }

  &__calendar-grid {
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    gap: 4px;
    text-align: center;
  }

  &__calendar-cell {
    display: flex;
    height: 56px;
    align-items: center;
    justify-content: center;
    border-radius: 6px;
    border: 1px solid #e5e7eb;
    background: white;
    padding: 8px;

    &--highlighted {
      box-shadow: 0 0 0 2px #2b7a78;
    }
  }

  &__calendar-cell-content {
    height: 32px;
    width: 100%;
    border-radius: 4px;
    background: rgba(43, 122, 120, 0.08);
  }

  &__status-items {
    margin-top: 16px;
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  &__status-item {
    display: flex;
    align-items: center;
    gap: 12px;
    border-radius: 12px;
    border: 1px solid #e5e7eb;
    padding: 12px;
    background: white;
  }

  &__status-icon {
    width: 16px;
    height: 16px;
    border-radius: 4px;
    background: #d1d5db;
    flex-shrink: 0;
  }

  &__status-text {
    font-size: 14px;
    color: #111827;
  }
}
</style>
